<template>
    <div class="position-relative">
        <!-- Header and Filter Section -->
        <b-card class="mt-4">
            <b-row class="align-items-end">
                <!-- Programme Dropdown -->
                <b-col md="3">
                    <b-form-group label="Choose Programme:" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedProgrammeId" :options="programmeOptions" class="form-control-sm"
                            @change="onProgrammeChange" />
                    </b-form-group>
                </b-col>

                <!-- Subgroup Dropdown -->
                <b-col md="3">
                    <b-form-group :label="`Choose ${subgroupLabel}:`" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedSubgroupId" :options="subgroupOptions" class="form-control-sm"
                            @change="onSubgroupChange" />
                    </b-form-group>
                </b-col>

                <!-- Category Dropdown -->
                <b-col md="4">
                    <b-form-group label="Choose Structure:" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedCategoryId" :options="categoryOptions" class="form-control-sm"
                            @change="onCategoryChange" />
                    </b-form-group>
                </b-col>

                <!-- Go Button -->
                <b-col md="2">
                    <b-button variant="primary" size="sm" class="w-100 font-weight-medium" @click="fetchStudentBillMappings"
                        :disabled="!selectedProgrammeId || !selectedSubgroupId || !selectedCategoryId">
                        Go
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- Student Table -->
        <div v-if="studentList.length">
            <div v-if="formError" class="alert alert-danger mb-3">{{ formError }}</div>

            <div class="d-flex justify-content-between align-items-center my-2 flex-wrap">
                <small class="text-muted">Enter an amount in a bill's header to apply it to every student.</small>
                <b-button size="sm" variant="secondary" v-b-modal.importAmountExcelModal>
                    Import Amount from Excel
                </b-button>
            </div>

            <div class="table-responsive">
                <table class="table table-striped table-bordered">
                    <thead class="thead-dark">
                        <tr>
                            <th>SI. No.</th>
                            <th>Student Name</th>
                            <th>Register No.</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th v-for="bill in categoryBills" :key="bill.id">
                                {{ bill.name }}
                                <small class="d-block text-muted font-weight-normal">{{ bill.component_name }}</small>
                                <b-form-input :value="headerAmounts[bill.id]" type="number" size="sm"
                                    placeholder="Amount for all" class="mt-1"
                                    @input="(val) => applyAmountToBill(bill.id, val)" />
                            </th>
                            <th>Total (₹)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(student, index) in studentList" :key="student.institution_user_id">
                            <td>{{ index + 1 }}</td>
                            <td>{{ student.name }}</td>
                            <td>{{ student.register_no }}</td>
                            <td>{{ student.email }}</td>
                            <td>{{ student.mobile }}</td>
                            <td v-for="bill in categoryBills" :key="bill.id">
                                <div v-if="student.bills[bill.id]" class="d-flex align-items-center">
                                    <b-form-input
                                        :value="student.bills[bill.id].debit_amount"
                                        @input="(val) => { student.bills[bill.id].debit_amount = val === '' ? null : Number(val); }"
                                        type="number" class="flex-grow-1" :disabled="student.bills[bill.id].is_paid" />
                                    <div v-if="student.bills[bill.id].is_paid" class="ms-2 tooltip-wrapper">
                                        <b-icon icon="lock-fill" variant="danger"></b-icon>
                                        <div class="custom-tooltip">
                                            <span>Payment Received</span>
                                        </div>
                                    </div>
                                </div>
                                <span v-else class="text-muted small">-</span>
                            </td>
                            <td class="font-weight-bold">₹{{ studentTotal(student).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-right mt-3">
                <b-button variant="primary" :disabled="updationEnable" @click="updateAmounts">
                    Update
                </b-button>
            </div>
        </div>

        <div v-else>
            <div v-if="loading" class="d-flex justify-content-center mt-3">
                <span>Loading...</span>
            </div>
            <div v-else-if="submitted && !categoryBills.length" class="d-flex justify-content-center mt-3">
                <span class="text-danger">No bills exist for this fee structure.</span>
            </div>
            <div v-else-if="submitted && studentList.length === 0" class="d-flex justify-content-center mt-3">
                <span class="text-danger">No students found for this fee structure in the selected class.</span>
            </div>
        </div>

        <!-- Import Amount Excel Modal -->
        <b-modal id="importAmountExcelModal" size="lg" v-model="viewImportAmountExcelModal"
            title="Import Amount from Excel" hide-footer>
            <div>
                <b-form-file v-if="!importAmountExcelFile" v-model="importAmountExcelFile" accept=".xlsx,.xls"
                    size="sm" @change="importAmountExcel" placeholder="Choose an Excel file or drop it here..."
                    drop-placeholder="Drop file here...">
                </b-form-file>

                <p class="mt-3" style="font-size: 14px; font-weight: 600">Notes:</p>
                <p><strong>1) The first row must be a header row, with one column per bill, using the exact bill
                        names shown as column headers in the table above (in any order).</strong></p>
                <p>2) The remaining rows must be in the same order as the students shown in the table above, one
                    row per student, with each cell holding that student's amount for that bill. Leave a cell blank
                    to skip updating that particular bill for that student.</p>
                <p>3) The row count (excluding the header) must match the number of students in the table.</p>
                <p style="color: red; font-weight: bold;">4) Cells for a student's bill where a payment has already
                    been received (locked amount field) will be skipped.</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import readXlsxFile from 'read-excel-file';

export default {
    data() {
        return {
            submitted: false,
            loading: false,
            updationEnable: false,

            viewImportAmountExcelModal: false,
            importAmountExcelFile: null,

            // Header "amount for all" box value per bill id
            headerAmounts: {},

            selectedProgrammeId: null,
            selectedSubgroupId: null,
            selectedCategoryId: null,

            subgroupLabel: '',

            programmeOptions: [{ value: null, text: 'Select an option' }],
            subgroupOptions: [{ value: null, text: 'Select an option' }],
            categoryOptions: [{ value: null, text: 'Select an option' }],
            bills: [],

            studentList: [],
            formError: '',
        };
    },

    computed: {
        categoryBills() {
            if (!this.selectedCategoryId) return [];
            return this.bills.filter((bill) => bill.category_id === this.selectedCategoryId);
        },
    },

    async created() {
        this.subgroupLabel = this.$store.getters.getCustomNamings?.userSubgroup?.custom_name || 'Usersubgroup';
        await this.loadProgrammes();
        await this.loadCategories();
        await this.loadBills();
    },

    methods: {
        onProgrammeChange() {
            this.selectedSubgroupId = null;
            this.studentList = [];
            this.submitted = false;
            this.loadSubgroups();
        },

        onSubgroupChange() {
            this.studentList = [];
            this.submitted = false;
        },

        onCategoryChange() {
            this.studentList = [];
            this.submitted = false;
        },

        async loadProgrammes() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`;
            const response = await this.$axios.get(url);
            this.programmeOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((programme) => ({
                    value: programme.id,
                    text: `${programme.name} (${programme.code}) ${programme.scheme || ''}`,
                })),
            ];
        },

        async getDefaultAcademicYear() {
            // $root.default_academic_year is only ever populated as a side effect of
            // visiting the Staff List / Set Academic Year pages, not during app
            // bootstrap - fetch it directly instead of trusting it's already set,
            // otherwise this silently queries academic_year=null and returns nothing.
            if (this.$root.default_academic_year[0]) return this.$root.default_academic_year[0];

            const url = `${this.$store.getters.getAPIKey.mainAPI}/user/setting/default_academic_year`;
            const response = await this.$axios.get(url);
            this.$root.default_academic_year[0] = `${response.data}`;
            return this.$root.default_academic_year[0];
        },

        async loadSubgroups() {
            try {
                const academicYear = await this.getDefaultAcademicYear();
                const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups/${this.selectedProgrammeId}/usersubgroups?academic_year=${academicYear}`;
                const response = await this.$axios.get(url);
                this.subgroupOptions = [
                    { value: null, text: 'Select an option' },
                    ...response.data
                        .filter((subgroup) => subgroup.type === null)
                        .map((subgroup) => ({
                            value: subgroup.id,
                            text: `${subgroup.name} (${subgroup.code})`
                        }))
                ];
            } catch (error) {
                error;
                this.subgroupOptions = [{ value: null, text: 'Error loading options' }];
            }
        },

        async loadCategories() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/feecategories`;
            const response = await this.$axios.get(url);
            this.categoryOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((category) => ({
                    value: category.id,
                    text: category.name,
                })),
            ];
        },

        async loadBills() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/feebills`;
            const response = await this.$axios.get(url);
            this.bills = response.data;
        },

        studentTotal(student) {
            return this.categoryBills.reduce((sum, bill) => {
                const entry = student.bills[bill.id];
                const amount = entry && entry.debit_amount !== null && entry.debit_amount !== undefined
                    ? Number(entry.debit_amount)
                    : 0;
                return sum + amount;
            }, 0);
        },

        async fetchStudentBillMappings() {
            if (!this.categoryBills.length) {
                this.studentList = [];
                this.submitted = true;
                return;
            }
            this.loading = true;
            this.formError = '';
            this.headerAmounts = {};
            try {
                const subgroupUrl = `${this.$store.getters.getAPIKey.mainAPI}/usersubgroup/${this.selectedSubgroupId}/users`;
                const subgroupResponse = await this.$axios.get(subgroupUrl);

                const subgroupStudentIds = subgroupResponse.data
                    .filter((user) => user.pivot.usersubgrouprole_id === 2)
                    .map((user) => user.pivot.institution_user_id);

                const billResponses = await Promise.all(
                    this.categoryBills.map((bill) =>
                        this.$axios.get(`${this.$store.getters.getAPIKey.mainAPI}/feebills/${bill.id}/transactions`)
                    )
                );

                const byStudent = {};

                billResponses.forEach((response, billIndex) => {
                    const bill = this.categoryBills[billIndex];

                    response.data.transactions
                        .filter((transaction) => !transaction.is_adjustment)
                        .filter((transaction) => subgroupStudentIds.includes(transaction.institution_user_id))
                        .forEach((transaction) => {
                            if (!byStudent[transaction.institution_user_id]) {
                                byStudent[transaction.institution_user_id] = {
                                    institution_user_id: transaction.institution_user_id,
                                    name: transaction.name,
                                    register_no: transaction.register_no,
                                    email: transaction.email,
                                    mobile: transaction.mobile,
                                    bills: {},
                                };
                            }
                            const student = byStudent[transaction.institution_user_id];

                            if (!student.bills[bill.id]) {
                                student.bills[bill.id] = { is_paid: false };
                            }
                            const entry = student.bills[bill.id];

                            // A paid bill has TWO non-adjustment rows: the original debit-only
                            // row and a separate credit-only row created at payment time - both
                            // share this same bill_id, and the order they arrive in isn't
                            // guaranteed. Merge field-by-field instead of Object.assign-ing
                            // whichever comes last, or one row's nulls blank out the other's data.
                            if (transaction.debit_amount !== null && transaction.debit_amount !== undefined) {
                                entry.transaction_id = transaction.transaction_id;
                                entry.debit_amount = parseFloat(transaction.debit_amount);
                                // Snapshot of what's actually saved in the DB right now -
                                // separate from debit_amount, which changes live as the
                                // staff member types, before it's ever submitted via Update.
                                entry.savedDebitAmount = parseFloat(transaction.debit_amount);
                            }

                            if (parseFloat(transaction.credit_amount) > 0) {
                                entry.is_paid = true;
                            }
                        });
                });

                this.studentList = Object.values(byStudent);
            } catch (error) {
                error;
                this.formError = 'Failed to load students for this fee structure. Please try again.';
            } finally {
                this.submitted = true;
                this.loading = false;
            }
        },

        async updateAmounts() {
            this.formError = '';
            this.updationEnable = true;

            try {
                await Promise.all(
                    this.categoryBills.map((bill) => {
                        const transactions = this.studentList
                            .map((student) => student.bills[bill.id])
                            .filter((entry) => entry && !entry.is_paid && entry.transaction_id)
                            .map((entry) => ({
                                transaction_id: entry.transaction_id,
                                debit_amount: entry.debit_amount ?? 0, // a blank amount is saved as 0
                            }));

                        if (!transactions.length) return Promise.resolve();

                        const url = `${this.$store.getters.getAPIKey.mainAPI}/feebills/${bill.id}/transactions`;
                        return this.$axios.put(url, { transactions });
                    })
                );

                // These amounts are now saved - reflect that immediately without
                // waiting for a fresh Go/fetch.
                this.studentList.forEach((student) => {
                    this.categoryBills.forEach((bill) => {
                        const entry = student.bills[bill.id];
                        if (entry && !entry.is_paid) {
                            entry.debit_amount = entry.debit_amount ?? 0;
                            entry.savedDebitAmount = entry.debit_amount;
                        }
                    });
                });

                this.$toast.success('Amounts updated successfully.', {
                    position: 'top',
                    duration: 4000,
                });

                this.headerAmounts = {};
            } catch (error) {
                this.formError = error.response?.data?.message || 'Failed to update amounts. Please try again.';
            } finally {
                this.updationEnable = false;
            }
        },

        // Typing in a bill's header box fills that amount into every unpaid
        // student entry for the bill. Clearing the box leaves entries as they are.
        applyAmountToBill(billId, value) {
            this.$set(this.headerAmounts, billId, value);
            if (value === null || value === '' || isNaN(value)) return;
            const amount = Number(value);
            this.studentList.forEach((student) => {
                const entry = student.bills[billId];
                if (entry && !entry.is_paid) {
                    this.$set(entry, 'debit_amount', amount);
                }
            });
        },

        async importAmountExcel(event) {
            const input = event.target;

            try {
                const rows = await readXlsxFile(input.files[0]);

                if (rows.length < 2) {
                    throw new Error('Excel file must have a header row of bill names followed by one row per student');
                }

                const [headerRow, ...dataRows] = rows;

                if (dataRows.length !== this.studentList.length) {
                    throw new Error('Row count (excluding the header) does not match the number of students in the table');
                }

                const columnBills = headerRow.map((header) => {
                    const label = String(header ?? '').trim().toLowerCase();
                    const bill = this.categoryBills.find((b) => b.name.trim().toLowerCase() === label);
                    if (!bill) {
                        throw new Error(`Unrecognized bill column header: "${header}"`);
                    }
                    return bill;
                });

                dataRows.forEach((row, rowIndex) => {
                    const student = this.studentList[rowIndex];

                    columnBills.forEach((bill, colIndex) => {
                        const value = row[colIndex];
                        if (value === null || value === undefined || value === '') return;
                        if (isNaN(value)) {
                            throw new Error(`Invalid amount at row ${rowIndex + 2}, column "${bill.name}"`);
                        }

                        const entry = student.bills[bill.id];
                        if (entry && !entry.is_paid) {
                            entry.debit_amount = parseFloat(value);
                        }
                    });
                });

                this.$toast.success('Amounts imported successfully');
            } catch (e) {
                this.$toast.error(e.message);
            } finally {
                input.value = '';
                this.importAmountExcelFile = null;
                this.viewImportAmountExcelModal = false;
            }
        },
    },
};
</script>

<style scoped>
.required-label::after {
    content: '*';
    color: red;
    margin-left: 4px;
}

.tooltip-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.custom-tooltip {
    visibility: hidden;
    background-color: #000;
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    position: absolute;
    z-index: 999;
    /* Above and right-anchored, so it stays inside the table box (which
       hides vertical overflow) and never widens it into a horizontal scroll. */
    bottom: 100%;
    right: 0;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.3s;
    margin-bottom: 5px;
    font-size: 13px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-wrapper:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
}
</style>
